import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Component: Accordion
 * ------------------------------------------------------------------------------
 * ARIA accessible accordion to collapse/expand content in a tabbed layout.
 *
 * @namespace accordion
 */

import cssClasses from '~/helpers/cssClasses';
import { extendDefaults, on } from '~/helpers/utils';
/**
 * DOM selectors.
 */

const selectors = {
  container: '[js-accordion="container"]',
  content: '[js-accordion="content"]',
  control: '[js-accordion="control"]',
  item: '[js-accordion="item"]'
};
/**
 * Attribute and data selectors.
 */

const dataSelectors = {
  accordion: 'js-accordion'
};
/**
 * Export a new accordion instance.
 * @param {String} selector - The accordion item.
 * @param {Object} config - Configuration.
 */

export default ((selector, config) => {
  /**
   * Instance default settings.
   */
  const defaults = {
    singleOpen: true,
    expandInitial: false,
    expandAll: false
  };
  /**
   * Instance settings.
   */

  const settings = extendDefaults(defaults, config);
  /**
   * DOM node selectors.
   */

  const nodeSelectors = {
    container: document.querySelector(selectors.container),
    controls: [...document.querySelector(selector).querySelectorAll(selectors.control)],
    items: [...document.querySelector(selector).querySelectorAll(selectors.item)]
  };
  /**
   * Initialise component.
   */

  function init() {
    setDefaultState();
    setEventListeners();
  }
  /**
   * Set click events on items.
   */


  function setEventListeners() {
    on('click', nodeSelectors.container, handleClickEvent);
    on('keydown', nodeSelectors.container, handleKeyboardEvent);
  }
  /**
   * Set all default states based on settings.
   */


  function setDefaultState() {
    if (settings.expandAll) {
      expandAllItems();
    }

    if (settings.expandInitial) {
      expandInitialItem();
    }
  }
  /**
    * Handle toggle event on accordion control click.
    * @param {Event} event - Click event.
    */


  function handleClickEvent(event) {
    if (!isTargetControl(event.target)) {
      return;
    }

    event.preventDefault();
    toggleItem(event.target);
  }
  /**
   * Handle keyboard behaviour on the main accordion container.
   * @param {Event} event - Keydown event.
   */


  function handleKeyboardEvent(event) {
    if (!isTargetControl(event.target)) {
      return;
    }

    if (isKeyPressArrowUp(event) || isKeyPressArrowDown(event)) {
      handleArrowEvents(event);
      return;
    }

    if (isKeyPressHome(event) || isKeyPressEnd(event)) {
      handleHomeEndEvents(event);
    }
  }
  /**
   * Handle up arrow events when control is currently focused.
   * - When focus is on an accordion header, moves focus to the previous accordion header.
   * - When focus is on first accordion header, moves focus to last accordion header.
   * @param {Event} event - Keydown event.
   */


  function handleArrowEvents(event) {
    event.preventDefault();
    const index = nodeSelectors.controls.indexOf(event.target);
    const direction = isKeyPressArrowUp(event) ? -1 : 1;
    const length = nodeSelectors.controls.length;
    const newIndex = (index + length + direction) % length;
    nodeSelectors.controls[newIndex].focus();
  }
  /**
   * Handle events when Home or End keyboard events are pressed when control is focused.
   * - Home: When focus is on an accordion header, moves focus to the first accordion header.
   * - End: When focus is on an accordion header, moves focus to the last accordion header.
   * @param {Event} event - Keydown on arrow up event.
   */


  function handleHomeEndEvents(event) {
    event.preventDefault();

    if (isKeyPressHome(event)) {
      nodeSelectors.controls[0].focus();
      return;
    }

    if (isKeyPressEnd(event)) {
      nodeSelectors.controls[nodeSelectors.controls.length - 1].focus();
    }
  }
  /**
   * Expand targeted item and set ARIA values.
   * @param {HTMLElement} element - The accordion item.
   */


  function expandItem(element) {
    element.classList.add(cssClasses.active);
    element.querySelector(selectors.control).setAttribute('aria-expanded', true);
  }
  /**
   * Hide targeted item and set ARIA values.
   * @param {HTMLElement} element - The accordion item.
   */


  function collapseItem(element) {
    element.classList.remove(cssClasses.active);
    element.querySelector(selectors.control).setAttribute('aria-expanded', false);
  }
  /**
   * Check if item is active.
   * @param {HTMLElement} element - The accordion item.
   * @returns {Boolean}
   */


  function isItemExpanded(element) {
    return element.classList.contains(cssClasses.active);
  }
  /**
   * Hide all unselected items.
   * @param {HTMLElement} element - The accordion item.
   */


  function collapseUnselectedItems(element) {
    nodeSelectors.items.forEach(item => {
      if (isItemExpanded(item) && item !== element) {
        collapseItem(item);
      }
    });
  }
  /**
   * Expand all items in accordion.
   */


  function expandAllItems() {
    nodeSelectors.items.forEach(element => {
      expandItem(element);
    });
  }
  /**
   * Expand initial item in accordion.
   */


  function expandInitialItem() {
    nodeSelectors.items.forEach((element, index) => {
      if (index === 0) {
        expandItem(element);
        return;
      }

      collapseItem(element);
    });
  }
  /**
   * Collapse all items in accordion.
   */


  function collapseAllItems() {
    nodeSelectors.items.forEach(item => {
      collapseItem(item);
    });
  }
  /**
   * Toggle targeted accordion item.
   * @param {HTMLElement} control - The clicked accordion control.
   */


  function toggleItem(control) {
    const item = control.closest(selectors.item);

    if (settings.singleOpen) {
      collapseUnselectedItems(item);
    }

    if (isItemExpanded(item)) {
      collapseItem(item);
      return;
    }

    expandItem(item);
  }
  /**
   * Check if keycode event is a down arrow or page down.
   * @param {Event} event - Keydown event.
   * @returns {Boolean}
   */


  function isKeyPressArrowDown(event) {
    return event.keyCode === 40 || event.keyCode === 34;
  }
  /**
   * Check if keycode event is an up arrow or page up.
   * @param {Event} event - Keydown event.
   * @returns {Boolean}
   */


  function isKeyPressArrowUp(event) {
    return event.keyCode === 38 || event.keyCode === 33;
  }
  /**
   * Check if keycode event is end.
   * @param {Event} event - Keydown event.
   * @returns {Boolean}
   */


  function isKeyPressEnd(event) {
    return event.keyCode === 35;
  }
  /**
   * Check if keycode event is home.
   * @param {Event} event - Keydown event.
   * @return {Boolean}
   */


  function isKeyPressHome(event) {
    return event.keyCode === 36;
  }
  /**
   * Check if the click target is an accordion control.
   * @param {HTMLElement} target - Clicked target to check quick view attributes against.
   * @returns {Boolean}
   */


  function isTargetControl(target) {
    return typeof target.attributes[dataSelectors.accordion] !== 'undefined' && target.attributes[dataSelectors.accordion].nodeValue === 'control';
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init,
    collapseAllItems,
    expandAllItems,
    expandInitialItem
  });
});